'use client'

import { ArrowLeft24 } from '@vinted/monochrome-icons'
import { Button, Divider, Icon, Navigation } from '@vinted/web-ui'
import { useEffect, useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'

import { useAbTest, useTrackAbTestCallback } from '@marketplace-web/shared/ab-tests'
import { useTracking } from '@marketplace-web/shared/event-tracker'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { ScrollableArea } from '@marketplace-web/shared/ui-helpers'
import { useLatestCallback } from '@marketplace-web/shared/ui-state'
import {
  FacetedCategoryDto,
  CatalogAttribute,
  Filter,
  SELECT_DROPDOWN_MAX_HEIGHT,
  SELECT_DROPDOWN_MIN_WIDTH,
} from '@marketplace-web/domain/catalog-data'

import { clickFilterEvent, selectCatalogEvent } from '_libs/common/event-tracker/events'
import { Screen } from 'constants/tracking/screens'

import { useCatalogContext } from '../../../../CatalogProvider'
import { useFacetedCategoriesContext } from '../../../../FacetedCategoriesProvider'
import useFetchDynamicFilters from '../../../../hooks/useFetchDynamicFilters'
import FilterDropdown from '../../common/FilterDropdown'

import CatalogFilterFacetedBase from './CatalogFilterFacetedBase'
import useNavStack from './useNavStack'

const CatalogFilterFacetedDropdown = () => {
  const translate = useTranslate('catalog.filters')
  const { track } = useTracking()
  const {
    state: {
      filters: { catalogIds },
      dynamicFilters: { selectedDynamicFilters },
      searchSession: { globalSearchSessionId, globalCatalogBrowseSessionId, sessionId },
    },
  } = useCatalogContext()
  const searchSessionId = sessionId

  const holdoutQ4 = useAbTest('buyer_domain_holdout_2024q4')?.variant !== 'off'
  const facetedCategoriesAb = useAbTest('scoped_search_faceted_categories_v2')
  const trackAbTest = useTrackAbTestCallback()

  const fetchDynamicFilters = useFetchDynamicFilters()

  const { categories } = useFacetedCategoriesContext()
  const [navStack, setNavStack] = useNavStack(categories, catalogIds)

  const seenItems = useRef(new Set<string>())
  const filteringSessionId = useRef(uuid())

  useEffect(() => {
    seenItems.current.clear()
  }, [navStack])

  const [forceClose, setForceClose] = useState(false)

  const handleOnFilterClick = () => {
    track(
      clickFilterEvent({
        filter: Filter.Category,
        screen: Screen.CatalogFilters,
        searchSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
      }),
    )
  }

  const trackCatalogSelection = (ids: Array<number>) => {
    track(
      selectCatalogEvent({
        catalogIds: ids,
        attributeId: CatalogAttribute.Filter,
      }),
    )
  }

  const handleLeafClick = (category: FacetedCategoryDto) => {
    const categoryId = Number(category.id)

    let newSelectedIds: Array<number> = []

    if (catalogIds.includes(categoryId)) {
      newSelectedIds = catalogIds.filter(id => id !== categoryId)
    } else {
      newSelectedIds = [categoryId]
    }

    fetchDynamicFilters({ selectedDynamicFilters, selectedFilters: { catalogIds: newSelectedIds } })
    trackCatalogSelection(newSelectedIds)

    setForceClose(true)
  }

  const handleClose = useLatestCallback(() => {
    setForceClose(false)

    filteringSessionId.current = uuid()
    seenItems.current.clear()
  })

  const handleOpen = useLatestCallback(() => {
    if (holdoutQ4) trackAbTest(facetedCategoriesAb)
  })

  const renderNavigation = (category: FacetedCategoryDto, back: () => void) => {
    return (
      <>
        <Navigation
          left={
            <Button
              aria={{
                'aria-label': translate('a11y.actions.back'),
              }}
              styling={Button.Styling.Flat}
              theme="amplified"
              icon={<Icon name={ArrowLeft24} />}
              onClick={back}
              testId="catalog-filter-go-back"
            />
          }
          body={category.title}
        />
        <Divider />
      </>
    )
  }

  return (
    <div className="u-ui-margin-right-regular u-ui-margin-bottom-regular">
      <FilterDropdown
        onOpen={handleOpen}
        onClose={handleClose}
        onFilterClick={handleOnFilterClick}
        useForceClose={forceClose}
        title={translate('catalog.name')}
        minWidth={SELECT_DROPDOWN_MIN_WIDTH}
        areAnyOfItemsSelected={catalogIds.length !== 0}
        testId="catalog--catalog-filter"
        useChipAsTrigger
      >
        <ScrollableArea maxHeight={SELECT_DROPDOWN_MAX_HEIGHT}>
          <CatalogFilterFacetedBase
            fromScreen={Screen.Catalog}
            seenItems={seenItems}
            filteringSessionId={filteringSessionId}
            onClickLeaf={handleLeafClick}
            renderNavigation={renderNavigation}
            navStack={navStack}
            setNavStack={setNavStack}
            categories={categories || []}
            selectedIds={catalogIds}
          />
        </ScrollableArea>
      </FilterDropdown>
    </div>
  )
}

export default CatalogFilterFacetedDropdown
